import { ComponentFactory, ViewContainerRef } from "@angular/core";
import { PopoverController } from "@ionic/angular";
import { BehaviorSubject, Subscription } from "rxjs";
import { combineLatest } from 'rxjs';
import { DynamicGraphAdditionalInput, GraphDataConfig } from "../objects/config";
import { ConfigDataService } from "../services/config-data.service";
import { GraphDataService } from "../services/graph-data-service.service";
import { ViewPeriodService } from "../services/view-period.service";
import { GraphResolverBase } from "./GraphResolverBase";
import { DynamicEchartComponent } from "../shared-components/dynamic/dynamic-echart/dynamic-echart.component";
import { EChartsOption } from "echarts";
import { getAdditionalInput } from "../helpers/DynamicGraphHelper";
import { GraphDependency } from "../enum/graph-dependency.enum";

// Fixed list of month names for monthly charts
const FULL_MONTH_LIST = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'June',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export class SimpleEChartsResolver extends GraphResolverBase {
    public async createComponent(
        componentFactory: ComponentFactory<unknown>,
        additionalInput: string | DynamicGraphAdditionalInput | undefined,
        configDataService: ConfigDataService,
        graphDataService: GraphDataService,
        _popoverController: PopoverController,
        viewPeriodService: ViewPeriodService,
        viewContainerRef: ViewContainerRef,
        subscription: Subscription
    ) {
        const chartTitleKey = (getAdditionalInput(additionalInput, 'chartTitleKey') as string);
        const datasourceKey = (getAdditionalInput(additionalInput, 'datasourceKey') as string);
        const echartsTemplate = (getAdditionalInput(additionalInput, 'echartsTemplate') as string);
        const shouldShowSelector = (getAdditionalInput(additionalInput, 'useSelector') as boolean);
        const multipleSelector = (getAdditionalInput(additionalInput, 'multipleSelected') as boolean);

        const barChartOption: EChartsOption = {
            color: ['#91CC75', '#F39C12', '#2f4554', '#61a0a8'], // ALL = green, Gate 3 = orange, Gate 7 = dark blue, BTS = light blue
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' }
            },
            legend: {
                bottom: 0,
                left: 'center',
                textStyle: {
                    color: '#fff'
                },
                itemWidth: 14,
                itemHeight: 14,
            },
            grid: {
                left: '3%',
                right: '4%',
                top: '2.5%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: viewPeriodService.DAY_LIST.slice(0, viewPeriodService.DAY_LIST.length - 1),
                axisTick: { alignWithLabel: true },
                axisLabel: {
                    color: '#fff'
                }
            },
            yAxis: {
                show: false,
            },
            series: [
                {
                    name: 'All',
                    type: 'bar',
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 12,
                        color: '#fff',
                        textShadowBlur: 0,
                        textShadowColor: 'transparent',
                        textShadowOffsetX: 0,
                        textShadowOffsetY: 0,
                        formatter: params => {
                            const val = (params as any).value;
                            if (typeof val === 'number') {
                                return val >= 1000
                                    ? `${(val / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 }).replace(/\.0$/, '')}K`
                                    : val.toLocaleString();
                            }
                            return val;
                        }
                    },
                    data: []
                },
                {
                    name: 'Gate 3',
                    type: 'bar',
                    barGap: 0,
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 12,
                        color: '#fff',
                        textShadowBlur: 0,
                        textShadowColor: 'transparent',
                        textShadowOffsetX: 0,
                        textShadowOffsetY: 0,
                        formatter: params => {
                            const val = (params as any).value;
                            if (typeof val === 'number') {
                                return val >= 1000
                                    ? `${(val / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 }).replace(/\.0$/, '')}K`
                                    : val.toLocaleString();
                            }
                            return val;
                        }
                    },
                    data: []
                },
                {
                    name: 'Gate 7',
                    type: 'bar',
                    barGap: 0,
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 12,
                        color: '#fff',
                        textShadowBlur: 0,
                        textShadowColor: 'transparent',
                        textShadowOffsetX: 0,
                        textShadowOffsetY: 0,
                        formatter: params => {
                            const val = (params as any).value;
                            if (typeof val === 'number') {
                                return val >= 1000
                                    ? `${(val / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 }).replace(/\.0$/, '')}K`
                                    : val.toLocaleString();
                            }
                            return val;
                        }
                    },
                    data: []
                },
                {
                    name: 'BTS',
                    type: 'bar',
                    label: {
                        show: true,
                        position: 'top',
                        fontSize: 12,
                        color: '#fff',
                        textShadowBlur: 0,
                        textShadowColor: 'transparent',
                        textShadowOffsetX: 0,
                        textShadowOffsetY: 0,
                        formatter: params => {
                            const val = (params as any).value;
                            if (typeof val === 'number') {
                                return val >= 1000
                                    ? `${(val / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 }).replace(/\.0$/, '')}K`
                                    : val.toLocaleString();
                            }
                            return val;
                        }
                    },
                    data: []
                },
            ]
        };
        const demographicOption: EChartsOption = {
            color: ['#76B1EF', '#DE887D'], // Female, Male
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    // params[0] is Female, params[1] is Male
                    const f = params[0], m = params[1];
                    return `${f.name}<br/>${f.seriesName}: ${Math.abs(f.value)}%<br/>${m.seriesName}: ${m.value}%`;
                }
            },
            legend: {
                data: ['Female', 'Male'],
                bottom: 0,
                left: 'center',
                textStyle: {
                    color: '#fff'
                },
                itemWidth: 14,
                itemHeight: 14,
            },
            grid: {
                left: '3%',
                right: '4%',
                top: '2.5%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                min: -100,
                max: 100,
                axisLabel: {
                    formatter: v => Math.abs(v) + '%',
                    color: '#fff'
                },
                // splitLine: { lineStyle: { color: '#eee' } },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'category',
                inverse: true,
                data: [
                    'Seniors\n(55+ yr)',
                    'Adults\n(41–55 yr)',
                    'Young Adults\n(25–40 yr)',
                    'Teenagers\n(13–24 yr)',
                    'Children\n(1–12 yr)'
                ],
                axisLabel: { color: '#fff' },
                axisLine: { show: false },
                axisTick: { show: false }
            },
            series: [
                {
                    name: 'Female',
                    type: 'bar',
                    // no stack or barGap: they both start at 0
                    label: {
                        show: true,
                        position: 'left',
                        color: '#fff',
                        formatter: p => Math.abs(p.value as number) + '%'
                    },
                    data: [],
                    stack: 'total',
                },
                {
                    name: 'Male',
                    type: 'bar',
                    label: {
                        show: true,
                        position: 'right',
                        color: '#fff',
                        formatter: p => p.value + '%'
                    },
                    data: [],
                    stack: 'total',
                }
            ]
        };

        const backgroundOfOriginOption: EChartsOption = {
            tooltip: {
                trigger: 'item',
                // name, value, and percent
                formatter: (params) => {
                    const p = params as any;
                    return `${p.name}: ${p.percent.toFixed(0)}%`;
                }
            },
            legend: {
                bottom: '5%',
                left: 'center',
                itemWidth: 14,
                itemHeight: 14,
                data: ['Asian', 'Western', 'Middle-east', 'Indian', 'Africa'],
                textStyle: {
                    color: '#fff'
                }
            },
            color: [
                '#3366CC', // Asian
                '#89CFF0', // Western
                '#61A0A8', // Middle-east
                '#E9BF3B', // Indian
                '#F39C12'  // Africa
            ],
            series: [
                {
                    name: 'Background of Origin',
                    type: 'pie',
                    radius: ['40%', '70%'],     // inner and outer radius
                    center: ['50%', '45%'],      // slightly lower than center
                    avoidLabelOverlap: false,
                    label: {
                        show: true,
                        position: 'outside',
                        formatter: (params) => {
                            const p = params as any;
                            return `${p.name}: ${p.percent.toFixed(0)}%`;
                        },
                        color: '#fff',
                        textShadowBlur: 0,
                        textShadowColor: 'transparent',
                        textShadowOffsetX: 0,
                        textShadowOffsetY: 0,

                    },
                    labelLine: {
                        show: true,
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 16,
                            fontWeight: 'bold',
                            formatter: (params) => {
                                const p = params as any;
                                return `${p.name}: ${p.percent.toFixed(0)}%`;
                            },
                            color: '#fff',
                            textShadowBlur: 0,
                            textShadowColor: 'transparent',
                            textShadowOffsetX: 0,
                            textShadowOffsetY: 0,
                        }
                    },
                    data: []
                }
            ]

        };

        const mappingEchartsConfig: { [template: string]: EChartsOption } = {
            'BAR': barChartOption,
            'DEMOGRAPHICS': demographicOption,
            'BACKGROUND_OF_ORIGIN': backgroundOfOriginOption
        }

        const chartOption = mappingEchartsConfig[echartsTemplate];
        const chartOption$ = new BehaviorSubject<EChartsOption>(chartOption);
        const selectOption: { [selectorName: string]: GraphDataConfig & { selected?: boolean; } } = {
            'all': { name: "", args: [], selected: true },
            'signage_g3': { name: "", args: [], selected: multipleSelector },
            'signage_g7': { name: "", args: [], selected: multipleSelector },
            'signage_bts': { name: "", args: [], selected: multipleSelector },
        }
        const selectedOption$ = new BehaviorSubject<{ [selectorName: string]: GraphDataConfig & { selected?: boolean; } }>(selectOption);

        graphDataService.baseGraphData.addDependency([GraphDependency.LANDMARK_AREA_VISITOR_PROFILE_CROSS]);

        if (datasourceKey === 'LANDMARK_PROFILE_ENTRANCE_EXIT') {
            combineLatest([
                graphDataService.landmarkAreaEntranceExitData$,
                selectedOption$
            ]).subscribe(([data, selection]) => {
                if (data) {
                    const option = barChartOption as EChartsOption;
                    // Categories for x-axis
                    const categories = viewPeriodService.DAY_LIST.slice(0, viewPeriodService.DAY_LIST.length - 1);
                    (option.xAxis as any).data = categories;
                    // Conditionally include series data based on user-selected options
                    option.series[1].data = selection['signage_g3'].selected
                        ? data.signage_g3.entrance
                        : categories.map(() => null);
                    option.series[2].data = selection['signage_g7'].selected
                        ? data.signage_g7.entrance
                        : categories.map(() => null);
                    option.series[3].data = selection['signage_bts'].selected
                        ? data.signage_bts.entrance
                        : categories.map(() => null);

                    // ALL series data
                    const g7Data = data.signage_g7.entrance;
                    const btsData = data.signage_bts.entrance;
                    const g3Data = data.signage_g3?.entrance || categories.map(() => 0);
                    option.series[0].data = selection['all'].selected
                        ? g7Data.map((g7Val, i) => (g7Val || 0) + (btsData[i] || 0) + (g3Data[i] || 0))
                        : categories.map(() => null);
                    // Update legend items based on selection
                    const legendItems: string[] = [];
                    if (selection['all'].selected) { legendItems.push('All'); }
                    if (selection['signage_g3'].selected) { legendItems.push('Gate 3'); }
                    if (selection['signage_g7'].selected) { legendItems.push('Gate 7'); }
                    if (selection['signage_bts'].selected) { legendItems.push('BTS'); }
                    // Show only selected legend entries
                    (option.legend as any).data = legendItems;
                    // Hide entire legend if none selected
                    (option.legend as any).show = legendItems.length > 0;
                    chartOption$.next(option);
                }
            });
        } else if (datasourceKey === 'LANDMARK_DEMOGRAPHICS') {
            combineLatest([
                graphDataService.landmarkAreaDemographicsData$,
                selectedOption$
            ]).subscribe(([data, selection]) => {
                if (data) {
                    const option = demographicOption as EChartsOption;
                    const ageCategories = ['seniors', 'adults', 'young_adults', 'teenagers', 'children'];
                    // Determine which signage to use
                    let signageKey: 'signage_g3' | 'signage_g7' | 'signage_bts' | 'all' | null = null;
                    if (selection['all'].selected) {
                        signageKey = 'all';
                    } else if (selection['signage_g7'].selected) {
                        signageKey = 'signage_g7';
                    } else if (selection['signage_bts'].selected) {
                        signageKey = 'signage_bts';
                    } else if (selection['signage_g3'].selected) {
                        signageKey = 'signage_g3';
                    }
                    // Extract values per signageKey
                    let femaleValues: (number | null)[], maleValues: (number | null)[];
                    if (signageKey === 'all') {
                        femaleValues = ageCategories.map(cat =>
                            ((data.signage_g7.female[cat]?.entrance || 0) + (data.signage_bts.female[cat]?.entrance || 0) + (data.signage_g3?.female[cat]?.entrance || 0))
                        );
                        maleValues = ageCategories.map(cat =>
                            ((data.signage_g7.male[cat]?.entrance || 0) + (data.signage_bts.male[cat]?.entrance || 0) + (data.signage_g3?.male[cat]?.entrance || 0))
                        );
                    } else if (signageKey) {
                        const sd = (data as any)[signageKey];
                        femaleValues = ageCategories.map(cat => sd.female[cat]?.entrance || 0);
                        maleValues = ageCategories.map(cat => sd.male[cat]?.entrance || 0);
                    } else {
                        femaleValues = ageCategories.map(() => null);
                        maleValues = ageCategories.map(() => null);
                    }
                    // Compute overall totals safely
                    const totalFemale = femaleValues.reduce((sum, v) => sum + (v as number || 0), 0) || 1;
                    const totalMale = maleValues.reduce((sum, v) => sum + (v as number || 0), 0) || 1;
                    // Convert to percentage
                    option.series[0].data = signageKey
                        ? femaleValues.map(v => -Math.floor((v as number) / totalFemale * 100))
                        : ageCategories.map(() => null);
                    option.series[1].data = signageKey
                        ? maleValues.map(v => Math.floor((v as number) / totalMale * 100))
                        : ageCategories.map(() => null);
                    // Show or hide demographic legend
                    (option.legend as any).show = !!signageKey;
                    chartOption$.next(option);
                }
            });
        } else if (datasourceKey === 'LANDMARK_BACKGROUND_OF_ORIGIN') {
            combineLatest([
                graphDataService.landmarkAreaEthnicityData$,
                selectedOption$
            ]).subscribe(([data, selection]) => {
                if (data) {
                    const option = backgroundOfOriginOption as EChartsOption;
                    // Determine which signage to use
                    let signageKey: 'signage_g7' | 'signage_bts' | 'signage_g3' | 'all' | null = null;
                    if (selection['all'].selected) {
                        signageKey = 'all';
                    } else if (selection['signage_g7'].selected) {
                        signageKey = 'signage_g7';
                    } else if (selection['signage_bts'].selected) {
                        signageKey = 'signage_bts';
                    } else if (selection['signage_g3'].selected) {
                        signageKey = 'signage_g3';
                    }
                    const ethnicityCategories: { key: keyof typeof data.signage_g7; name: string }[] = [
                        { key: 'asian', name: 'Asian' },
                        { key: 'white', name: 'Western' },
                        { key: 'middle_eastern', name: 'Middle-east' },
                        { key: 'indian', name: 'Indian' },
                        { key: 'black', name: 'Africa' }
                    ];
                    let seriesData;
                    if (signageKey === 'all') {
                        seriesData = ethnicityCategories.map(cat => ({
                            value: (data.signage_g7[cat.key]?.entrance || 0) + (data.signage_bts[cat.key]?.entrance || 0) + (data.signage_g3?.[cat.key]?.entrance || 0),
                            name: cat.name
                        }));
                    } else if (signageKey) {
                        const sd = (data as any)[signageKey];
                        seriesData = ethnicityCategories.map(cat => ({
                            value: sd[cat.key]?.entrance || 0,
                            name: cat.name
                        }));
                    } else {
                        seriesData = [];
                    }
                    option.series[0].data = seriesData;
                    // Legend update logic remains unchanged; legend hides automatically if seriesData is empty
                    chartOption$.next(option);
                }
            });
        } else if (datasourceKey === 'LANDMARK_PROFILE_ENTRANCE_EXIT_MONTHLY') {
            graphDataService.baseGraphData.addDependency(GraphDependency.LANDMARK_AREA_VISITOR_PROFILE_CROSS_12_MONTHLY);
            combineLatest([
                graphDataService.landmarkAreaEntranceExitMonthlyData$,
                selectedOption$
            ]).subscribe(([data, selection]) => {
                if (data) {
                    const option = barChartOption as EChartsOption;
                    // Categories for x-axis
                    // Construct month labels with two-digit year suffix, e.g. 'Jan/25'
                    const fullYear = viewPeriodService.selectedDate.get('year');
                    const yearSuffix = (fullYear % 100).toString().padStart(2, '0');
                    const categories = FULL_MONTH_LIST.map(month => `${month}/${yearSuffix}`);
                    (option.xAxis as any).data = categories;
                    // Conditionally include series data based on user-selected options
                    option.series[1].data = selection['signage_g3'].selected
                        ? data.signage_g3.entrance
                        : categories.map(() => null);
                    option.series[2].data = selection['signage_g7'].selected
                        ? data.signage_g7.entrance
                        : categories.map(() => null);
                    option.series[3].data = selection['signage_bts'].selected
                        ? data.signage_bts.entrance
                        : categories.map(() => null);
                    // ALL series data
                    const g3Data = data.signage_g3?.entrance || categories.map(() => 0);
                    const g7Data = data.signage_g7.entrance;
                    const btsData = data.signage_bts.entrance;
                    option.series[0].data = selection['all'].selected
                        ? g7Data.map((g7Val, i) => (g7Val || 0) + (btsData[i] || 0) + (g3Data[i] || 0))
                        : categories.map(() => null);
                    // Update legend items based on selection
                    const legendItems: string[] = [];
                    if (selection['all'].selected) { legendItems.push('All'); }
                    if (selection['signage_g3'].selected) { legendItems.push('Gate 3'); }
                    if (selection['signage_g7'].selected) { legendItems.push('Gate 7'); }
                    if (selection['signage_bts'].selected) { legendItems.push('BTS'); }
                    // Show only selected legend entries
                    (option.legend as any).data = legendItems;
                    // Hide entire legend if none selected
                    (option.legend as any).show = legendItems.length > 0;
                    chartOption$.next(option);
                }
            });
        }

        const componentRef = viewContainerRef.createComponent(componentFactory);
        const comInstance = componentRef.instance as DynamicEchartComponent;
        comInstance.title = chartTitleKey;
        comInstance.isShow = true;
        comInstance.chartOption = chartOption;
        comInstance.chartOption$ = chartOption$;
        comInstance.shouldShowSelector = shouldShowSelector;
        comInstance.multipleSelector = multipleSelector;
        comInstance.selectedOption$ = selectedOption$;
        comInstance.selector = selectOption;
        return comInstance;
    }

}